import React, { useEffect, useRef } from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";

// agent-experience-redesign-v1.md §4.6 (D8) — right-click / kebab
// context menu rendered at the click coordinates. Items are passed
// as an `items` array; consumers wire the handlers (Open, Test in
// chat, Duplicate, Archive, Delete, Export tree, Publish to
// marketplace, Set as default for channel…).
//
// Closes on outside click + Esc. Auto-flips above when near the
// viewport bottom edge; flips left when near the right edge.

const MENU_W = 200;
const ITEM_H = 32;

export default function TileContextMenu({ x, y, items, onClose, testId }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose && onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose && onClose(); }
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Compute position with viewport-clamping.
  const dividerCount = items.filter(i => i.divider).length;
  const itemCount = items.length - dividerCount;
  const menuH = itemCount * ITEM_H + dividerCount * 9 + 8;
  const flipUp = y + menuH > window.innerHeight - 8;
  const flipLeft = x + MENU_W > window.innerWidth - 8;
  const top = flipUp ? Math.max(8, y - menuH) : Math.min(window.innerHeight - menuH - 8, y);
  const left = flipLeft ? Math.max(8, x - MENU_W) : Math.min(window.innerWidth - MENU_W - 8, x);

  return (
    <div
      ref={ref}
      role="menu"
      data-testid={testId}
      style={{
      position: "fixed", top, left, width: MENU_W,
      zIndex: 200,
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 8,
      boxShadow: MATERIALS?.spatialShadow ? MATERIALS.spatialShadow() : "0 12px 36px rgba(0,0,0,0.5)",
      padding: 4,
      display: "flex", flexDirection: "column",
    }}>
      {items.map((item, idx) => {
        if (item.divider) {
          return <div key={`d-${idx}`} style={{
            height: 1, background: COLORS.border, margin: "4px 6px",
          }} />;
        }
        return (
          <button
            key={item.key || item.label}
            type="button"
            role="menuitem"
            // `data-menu-item` carries the item's stable KEY, not its label.
            // The label is user-facing copy ("Test in chat") that is expected
            // to change; the key is the contract.
            data-testid="tile-menu-item"
            data-menu-item={item.key || ""}
            disabled={item.disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (item.disabled) return;
              onClose && onClose();
              item.onClick && item.onClick();
            }}
            style={{
              padding: "7px 10px", border: "none", borderRadius: 5,
              background: "transparent",
              color: item.danger ? (COLORS.pink || "#ef4444")
                : item.disabled ? COLORS.textDim
                : COLORS.text,
              fontSize: 12, fontWeight: 500,
              cursor: item.disabled ? "default" : "pointer",
              textAlign: "left", letterSpacing: "0.01em",
              transition: "background 0.12s",
              opacity: item.disabled ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!item.disabled) e.currentTarget.style.background = `${COLORS.accent}15`;
            }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            {item.label}
            {item.shortcut && (
              <span style={{ float: "right", color: COLORS.textDim, fontSize: 10, fontFamily: "monospace" }}>
                {item.shortcut}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
